import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Card } from '../../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Users, Trash2, Settings } from 'lucide-react';

export default function Teams() {
  const { orgId } = useParams();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: '',
  });

  useEffect(() => {
    fetchTeams();
  }, [orgId]);

  const fetchTeams = async () => {
    try {
      const response = await apiClient.get(`/enterprise/orgs/${orgId}/teams`);
      setTeams(response.data);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTeam = async () => {
    try {
      await apiClient.post(`/enterprise/orgs/${orgId}/teams`, newTeam);
      toast.success('Team created');
      setNewTeam({ name: '', description: '' });
      setDialogOpen(false);
      fetchTeams();
    } catch (error) {
      toast.error('Failed to create team');
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Are you sure you want to delete this team?')) return;

    try {
      await apiClient.delete(`/enterprise/orgs/${orgId}/teams/${teamId}`);
      toast.success('Team deleted');
      fetchTeams();
    } catch (error) {
      toast.error('Failed to delete team');
    }
  };

  return (
    <div className="p-8 space-y-6" data-testid="teams-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-gray-900">Teams</h1>
          <p className="text-gray-600 mt-1">Organize members into departments and teams</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-team-button">
              <Plus className="w-4 h-4 mr-2" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create Team</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Team Name *</Label>
                <Input
                  value={newTeam.name}
                  onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                  placeholder="e.g., Engineering, Sales, Support"
                  data-testid="team-name-input"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={newTeam.description}
                  onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                  placeholder="What does this team do?"
                  rows={3}
                />
              </div>
              <Button
                onClick={handleCreateTeam}
                disabled={!newTeam.name}
                className="w-full"
                data-testid="create-team-submit"
              >
                Create Team
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-6 rounded-lg animate-pulse">
              <div className="h-6 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </Card>
          ))}
        </div>
      ) : teams.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Card key={team.id} className="p-6 rounded-lg hover:shadow-md transition-all">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="w-10 h-10 rounded-lg bg-enterprise-slate/10 flex items-center justify-center mb-3">
                    <Users className="w-5 h-5 text-enterprise-slate" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 mb-1">
                    {team.name}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{team.description || 'No description'}</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                <span className="text-sm text-gray-600">
                  {team.membersCount || 0} members
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteTeam(team.id)}>
                    <Trash2 className="w-4 h-4 text-gray-400 hover:text-error" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 rounded-lg text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="font-heading text-xl font-semibold text-gray-900 mb-2">
            No teams yet
          </h3>
          <p className="text-gray-600 mb-6">
            Create teams to organize your members by department or function
          </p>
          <Button onClick={() => setDialogOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create First Team
          </Button>
        </Card>
      )}
    </div>
  );
}
